import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import {
  Phone, Calendar, CheckCircle2, ShieldCheck, Users, Car, Clock, MapPin,
  Stethoscope, Heart, Activity, Hospital, ArrowRight, Loader2, ChevronDown,
  Star, AlertCircle, Facebook, Calculator
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Reveal } from '@/components/ui/Reveal';
import { StarRating } from '@/components/ui/StarRating';
import CPAMBadge from '@/components/ui/CPAMBadge';
import { useSEO, buildBreadcrumb, buildFAQPage, SITE_URL } from '@/lib/seo';
import { trackPhoneClick, trackFormSubmit, trackLandingView, trackMobileCallButton, trackFacebookAmbulance03Click } from '@/lib/analytics';
import api from '@/lib/api';
import { parseApiError } from '@/lib/parseApiError';
import { toast } from 'sonner';

const PHONE_DISPLAY = '06 56 66 05 71';
const PHONE_TEL = 'tel:+33656660571';
const EMAIL_TO = 'ambulances03avermes@gmail.com';
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61593070187408';
const CANONICAL = `${SITE_URL}/ambulance-03-avermes`;
const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_ambulances-beaulon/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png';

const COMMUNES = [
  { name: 'Avermes', lat: 46.5833, lng: 3.3200, main: true },
  { name: 'Moulins', lat: 46.5667, lng: 3.3333, main: true },
  { name: 'Yzeure', lat: 46.5667, lng: 3.3667, main: true },
  { name: 'Saint-Ennemond', lat: 46.6500, lng: 3.4333, main: true },
  { name: 'Neuvy', lat: 46.5833, lng: 3.3000 },
  { name: 'Toulon-sur-Allier', lat: 46.5000, lng: 3.4000 },
  { name: 'Trévol', lat: 46.6500, lng: 3.3333 },
  { name: 'Bressolles', lat: 46.5333, lng: 3.3500 },
];

const FAQS = [
  { q: 'Comment réserver un transport sanitaire avec Ambulance 03 à Avermes ou Saint-Ennemond ?', a: `Appelez directement notre équipe au ${PHONE_DISPLAY} ou remplissez le formulaire ci-dessus. Nous vous rappelons ensuite pour confirmer les horaires, le mode de transport et les modalités administratives.` },
  { q: 'Quelles communes desservez-vous depuis Avermes / Saint-Ennemond ?', a: `Nous couvrons principalement Avermes, Moulins, Yzeure, Saint-Ennemond, Neuvy, Toulon-sur-Allier, Trévol et Bressolles. Nous intervenons plus largement sur tout le nord du département de l'Allier selon disponibilité.` },
  { q: 'Une prescription médicale est-elle obligatoire pour un remboursement CPAM ?', a: "Oui, pour que le transport soit remboursé par l'Assurance Maladie, une prescription médicale (bon de transport CERFA n°50742*04) délivrée par votre médecin est obligatoire, sauf en cas d'urgence vitale. Elle indique le mode de transport à utiliser (ambulance ou VSL)." },
  { q: 'Vos ambulances et VSL sont-ils conventionnés par l\'Assurance Maladie ?', a: "Oui. Toutes nos ambulances et nos VSL sont conventionnés CPAM. Nous appliquons la prise en charge directe (tiers payant) pour les patients éligibles : dialyse, chimiothérapie, radiothérapie, hospitalisations et consultations sur prescription." },
  { q: 'Quelle est la différence entre une ambulance et un VSL ?', a: "L'ambulance est utilisée lorsque le patient doit être transporté en position allongée ou nécessite une surveillance médicale. Le VSL (Véhicule Sanitaire Léger) est adapté aux patients pouvant voyager en position assise mais nécessitant une assistance. Le choix est indiqué par votre médecin sur la prescription." },
  { q: 'Intervenez-vous à domicile ?', a: "Oui, nous venons vous chercher à votre domicile (ou lieu de départ) et vous accompagnons jusqu'à votre destination (hôpital, clinique, centre de soins). Nous assurons également le retour à domicile." },
  { q: 'Peut-on être accompagné pendant le transport ?', a: "Oui, un accompagnant peut être présent lors du transport (généralement un proche). Merci de nous le préciser lors de la réservation pour que nous prévoyions la place nécessaire." },
  { q: 'Quels sont vos horaires ?', a: "Nous sommes joignables tous les jours pour organiser vos transports programmés. Les trajets peuvent être planifiés selon vos besoins (matin, journée, soirée), et nous nous adaptons aux horaires de vos rendez-vous médicaux." },
];

const makeIcon = (isMain) =>
  L.divIcon({
    className: '',
    html: `<div style="position:relative">
      ${isMain ? `<div style="position:absolute;inset:-10px;border-radius:9999px;background:#1F7AAE;opacity:.2;animation:pulse-ring 2.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite"></div>` : ''}
      <div style="width:${isMain ? 22 : 16}px;height:${isMain ? 22 : 16}px;border-radius:9999px;background:${isMain ? '#0E3A57' : '#1F7AAE'};border:3px solid white;box-shadow:0 6px 16px rgba(15,23,32,0.25);"></div>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

const INITIAL_FORM = {
  nom: '', prenom: '', telephone: '', email: '',
  date_souhaitee: '', heure_souhaitee: '',
  commune_depart: '', destination: '',
  type_transport: '', prescription_medicale: '',
  commentaires: '',
};

export default function Ambulance03Avermes() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [reviews, setReviews] = useState([]);

  useSEO({
    title: "Ambulance Avermes Saint-Ennemond | VSL Moulins | Ambulance 03",
    description: "Ambulance 03 — transport sanitaire conventionné à Avermes, Moulins, Yzeure et Saint-Ennemond. Ambulance et VSL conventionnés CPAM pour dialyse, hospitalisations, consultations. Appelez au 06 56 66 05 71.",
    keywords: "ambulance avermes, ambulance moulins, ambulance saint ennemond, ambulance yzeure, vsl moulins, vsl avermes, ambulance 03, transport sanitaire moulins, transport sanitaire allier, transport sanitaire conventionné, transport médical moulins, transport de patients moulins, ambulance conventionnée cpam, vsl conventionné cpam, ambulance neuvy, ambulance toulon-sur-allier, ambulance trévol, ambulance bressolles, transport dialyse moulins, transport chimiothérapie moulins",
    canonical: CANONICAL,
    ogImage: OG_IMAGE,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": ["MedicalBusiness", "LocalBusiness"],
        "@id": `${CANONICAL}#business`,
        "name": "Ambulance 03 – Avermes / Saint-Ennemond",
        "alternateName": ["Ambulance 03", "Ambulance 03 Avermes", "Ambulance 03 Saint-Ennemond"],
        "description": "Transport sanitaire conventionné en ambulance et VSL à Avermes, Saint-Ennemond, Yzeure et sur le secteur de Moulins. Prise en charge CPAM directe pour dialyse, chimiothérapie, hospitalisations, consultations et sorties d'hôpital.",
        "url": CANONICAL,
        "telephone": "+33656660571",
        "email": EMAIL_TO,
        "priceRange": "€€",
        "image": OG_IMAGE,
        "logo": OG_IMAGE,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Avermes",
          "addressLocality": "Avermes",
          "postalCode": "03000",
          "addressRegion": "Allier",
          "addressCountry": "FR"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 46.5833,
          "longitude": 3.3333
        },
        "areaServed": COMMUNES.map(c => ({ "@type": "City", "name": c.name })),
        "openingHoursSpecification": [{
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
          "opens": "00:00", "closes": "23:59"
        }],
        "medicalSpecialty": "MedicalTransport",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Transports sanitaires proposés",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport en ambulance conventionnée CPAM" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport en VSL conventionné CPAM" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport pour dialyse (ALD 100%)" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport pour chimiothérapie et radiothérapie" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport pour hospitalisations et sorties d'hôpital" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport pour consultations et examens" } }
          ]
        },
        "parentOrganization": {
          "@type": "Organization",
          "name": "Groupe Ambulances Beaulon",
          "url": SITE_URL
        },
        "sameAs": [
          "https://www.facebook.com/profile.php?id=61593070187408"
        ]
      },
      buildBreadcrumb([
        { name: 'Accueil', url: SITE_URL + '/' },
        { name: 'Nos implantations', url: SITE_URL + '/implantations' },
        { name: 'Ambulance 03 – Avermes / Saint-Ennemond', url: CANONICAL },
      ]),
      buildFAQPage(FAQS),
    ]
  });

  useEffect(() => {
    trackLandingView('ambulance-03-avermes');
    api.get('/avis').then(r => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type_transport || !form.prescription_medicale) {
      toast.error('Merci de renseigner le type de transport et la prescription médicale.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        // Normalise l'email : si vide, on n'envoie pas la clé pour respecter l'optionnalité côté backend
        email: form.email?.trim() || undefined,
        prescription_medicale: form.prescription_medicale === 'oui',
      };
      const res = await api.post('/ambulance03/request', payload);
      setSuccess(res.data);
      trackFormSubmit('ambulance03-landing');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = parseApiError(
        err,
        `Une erreur est survenue. Merci de nous appeler directement au ${PHONE_DISPLAY}.`
      );
      toast.error(msg, { duration: 6000 });
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div>
        <SuccessScreen data={success} onReset={() => { setSuccess(null); setForm(INITIAL_FORM); }} />
        <Ambulance03StickyMobileCTA />
      </div>
    );
  }

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-brand-50/40 to-white">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-100/40 blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-100/30 blur-3xl pointer-events-none"></div>

        <div className="container-app relative pt-10 sm:pt-14 pb-12 lg:pb-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Ambulance03Logo />
            </Reveal>
            <Reveal delay={80}>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <CPAMBadge variant="solid" size="sm" />
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-anthracite-700 bg-white/80 border rounded-full px-3 py-1">
                  <MapPin className="h-3 w-3 text-brand-700" /> Avermes · Moulins · Saint-Ennemond
                </span>
              </div>
            </Reveal>
            <Reveal delay={140}>
              <h2 className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight text-anthracite-950 leading-tight" data-testid="ambulance03-hero-title">
                Ambulance et VSL à <span className="text-brand-700">Avermes</span>, <span className="text-brand-700">Moulins</span> et <span className="text-brand-700">Saint-Ennemond</span>
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-4 text-base sm:text-lg text-anthracite-700 max-w-2xl leading-relaxed">
                Transport sanitaire conventionné pour <strong className="text-anthracite-900">consultations, hospitalisations, dialyses, rééducation, retours à domicile</strong> et transports programmés.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={PHONE_TEL}
                  onClick={() => trackPhoneClick('hero', PHONE_DISPLAY)}
                  className="inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white px-5 py-3.5 font-semibold shadow-[0_10px_28px_rgba(31,122,174,0.4)] transition-all hover:-translate-y-0.5"
                  data-testid="ambulance03-hero-call"
                >
                  <Phone className="h-5 w-5" />
                  <span className="flex flex-col items-start leading-none">
                    <span className="text-[10px] font-medium text-white/85 uppercase tracking-[0.12em]">Appeler maintenant</span>
                    <span className="text-base font-heading font-bold">{PHONE_DISPLAY}</span>
                  </span>
                </a>
                <a
                  href="#demande"
                  className="inline-flex items-center gap-2 rounded-xl bg-white border-2 border-brand-500 text-brand-700 hover:bg-brand-50 px-5 py-3.5 font-semibold transition-colors"
                  data-testid="ambulance03-hero-form-cta"
                >
                  <Calendar className="h-5 w-5" /> Demander un transport
                </a>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-anthracite-700">
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-700" /> Conventionné CPAM</span>
                <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-brand-700" /> Équipe diplômée</span>
                <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-brand-700" /> Rappel rapide</span>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackFacebookAmbulance03Click('hero')}
                  className="inline-flex items-center gap-1.5 text-brand-700 hover:text-brand-900 underline decoration-transparent hover:decoration-current transition-colors"
                  data-testid="ambulance03-facebook-hero"
                  aria-label="Suivez Ambulance 03 sur Facebook (nouvel onglet)"
                >
                  <Facebook className="h-4 w-4" /> Suivez Ambulance 03 sur Facebook
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={160}>
              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-brand-200/40 to-brand-50 blur-2xl"></div>
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-gradient-to-br from-anthracite-100 to-anthracite-200/40 shadow-card">
                  <img
                    src="https://customer-assets.emergentagent.com/job_ambulances-beaulon.com/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png"
                    onError={(e) => { e.currentTarget.src = 'https://customer-assets.emergentagent.com/job_ambulances-beaulon/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png'; }}
                    alt="Ambulance 03 Avermes Saint-Ennemond - véhicule sanitaire"
                    className="w-full h-full object-contain p-3"
                    loading="eager"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ POURQUOI CHOISIR ============ */}
      <section className="section-pad bg-white">
        <div className="container-app">
          <Reveal>
            <div className="max-w-2xl">
              <p className="badge-soft mb-3">Pourquoi nous choisir</p>
              <h2 className="h2 text-anthracite-950">Une équipe locale, des véhicules récents, une réactivité constante</h2>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: ShieldCheck, title: 'Ambulances et VSL conventionnés', desc: "Tous nos véhicules sont conventionnés par l'Assurance Maladie pour une prise en charge directe." },
              { icon: Users, title: 'Équipe diplômée', desc: "Ambulanciers Diplômés d'État (DEA), auxiliaires et chauffeurs formés au transport sanitaire." },
              { icon: Car, title: 'Véhicules récents', desc: "Une flotte moderne, régulièrement entretenue, équipée du matériel médical nécessaire." },
              { icon: Clock, title: 'Respect des horaires', desc: "Ponctualité stricte pour vos rendez-vous médicaux, dialyses et sorties d'hospitalisation." },
              { icon: CPAMIcon, title: 'Prise en charge CPAM', desc: "Selon prescription médicale (bon de transport CERFA). Nous vous accompagnons dans les démarches." },
              { icon: MapPin, title: 'Intervention Allier', desc: "Avermes, Moulins, Saint-Ennemond, Yzeure et tout le département de l'Allier selon disponibilité." },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 60}>
                <article className="card-base p-5 h-full flex flex-col" data-testid={`ambulance03-why-${i}`}>
                  <div className="h-11 w-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 font-heading font-semibold text-anthracite-950">{f.title}</h3>
                  <p className="mt-2 text-sm text-anthracite-700 leading-relaxed">{f.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="section-pad bg-anthracite-100/40">
        <div className="container-app">
          <Reveal>
            <div className="max-w-3xl">
              <p className="badge-soft mb-3">Nos services</p>
              <h2 className="h2 text-anthracite-950">Un transport adapté à chaque situation</h2>
              <p className="lead mt-3">Nous prenons en charge tous les transports sanitaires programmés dans l'Allier, sur prescription médicale.</p>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: 'Ambulance', title: 'Transport en ambulance', desc: 'Transport allongé avec surveillance médicale.' },
              { icon: 'Car', title: 'Transport en VSL', desc: 'Véhicule Sanitaire Léger, patient assis.' },
              { icon: Hospital, title: 'Hospitalisations', desc: 'Entrées et sorties d\'hôpital.' },
              { icon: Stethoscope, title: 'Consultations', desc: 'Rendez-vous chez spécialistes, examens, bilans.' },
              { icon: Heart, title: 'Dialyse', desc: 'Trajets réguliers vers centres de dialyse.' },
              { icon: Activity, title: 'Chimiothérapie', desc: 'Trajets vers centres de chimiothérapie (ALD 100%).' },
              { icon: Activity, title: 'Radiothérapie', desc: 'Séances de radiothérapie programmées.' },
              { icon: Activity, title: 'Rééducation', desc: 'Kiné, soins de suite, rééducation fonctionnelle.' },
              { icon: Hospital, title: 'Entrées et sorties d\'hôpital', desc: 'Retour à domicile en toute sécurité.' },
              { icon: MapPin, title: 'Transport longue distance', desc: 'Transferts inter-régionaux et nationaux.' },
              { icon: ShieldCheck, title: 'Transport conventionné', desc: 'Assurance Maladie, tarifs réglementés.' },
            ].map((s, i) => {
              const IconComp = typeof s.icon === 'string' ? IconByName : s.icon;
              return (
                <Reveal key={s.title} delay={i * 30}>
                  <article className="card-base p-4 h-full flex gap-3 items-start" data-testid={`ambulance03-service-${i}`}>
                    <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                      {typeof s.icon === 'string' ? <IconByName name={s.icon} /> : <s.icon className="h-5 w-5" />}
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-anthracite-950 text-sm">{s.title}</h3>
                      <p className="mt-1 text-xs text-anthracite-700 leading-relaxed">{s.desc}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <span>Ambulance 03 est un service de <strong>transport sanitaire programmé</strong>. En cas d'urgence vitale, composez immédiatement le <strong>15 (SAMU)</strong>.</span>
          </div>
        </div>
      </section>

      {/* ============ ZONE D'INTERVENTION ============ */}
      <section className="section-pad bg-white">
        <div className="container-app">
          <Reveal>
            <div className="max-w-2xl">
              <p className="badge-soft mb-3">Zone d'intervention</p>
              <h2 className="h2 text-anthracite-950">Nous intervenons sur tout le secteur de Moulins</h2>
              <p className="lead mt-3">Avermes, Moulins, Yzeure, Saint-Ennemond et communes environnantes. Plus largement, tout le département de l'Allier selon disponibilité.</p>
            </div>
          </Reveal>
          <div className="mt-8 grid lg:grid-cols-[1fr_1.4fr] gap-6">
            <div className="grid grid-cols-2 gap-2 content-start">
              {COMMUNES.map(c => (
                <div key={c.name} className={`rounded-lg border p-3 text-sm ${c.main ? 'border-brand-300 bg-brand-50' : 'border-border bg-white'}`}>
                  <div className="flex items-center gap-2">
                    <MapPin className={`h-4 w-4 ${c.main ? 'text-brand-700' : 'text-anthracite-500'}`} />
                    <span className={`font-medium ${c.main ? 'text-brand-800' : 'text-anthracite-900'}`}>{c.name}</span>
                  </div>
                </div>
              ))}
              <div className="col-span-2 mt-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-brand-500"></span>Communes principales</span>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border bg-white shadow-sm h-[380px] sm:h-[440px]" data-testid="ambulance03-map">
              <MapContainer center={[46.58, 3.35]} zoom={11} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Circle center={[46.58, 3.35]} radius={15000} pathOptions={{ color: '#1F7AAE', fillColor: '#1F7AAE', weight: 1, opacity: 0.4, fillOpacity: 0.08 }} />
                {COMMUNES.map(c => (
                  <Marker key={c.name} position={[c.lat, c.lng]} icon={makeIcon(c.main)}>
                    <Popup>
                      <div className="font-heading font-semibold text-anthracite-950">{c.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Zone couverte par Ambulance 03</div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FORMULAIRE ============ */}
      <section id="demande" className="section-pad bg-gradient-to-b from-brand-50/50 to-white scroll-mt-24">
        <div className="container-app max-w-3xl">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <p className="badge-soft mb-3 mx-auto">Demande de transport</p>
              <h2 className="h2 text-anthracite-950">Organisons votre trajet en 1 minute</h2>
              <p className="lead mt-3">Remplissez ce formulaire, notre équipe vous rappelle rapidement pour confirmer.</p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <form onSubmit={submit} className="mt-8 card-base p-6 sm:p-8 space-y-5" data-testid="ambulance03-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom *" value={form.nom} onChange={v => upd('nom', v)} required testid="a03-nom" />
                <Field label="Prénom *" value={form.prenom} onChange={v => upd('prenom', v)} required testid="a03-prenom" />
                <Field label="Téléphone *" type="tel" value={form.telephone} onChange={v => upd('telephone', v)} required testid="a03-telephone" />
                <Field label="Email (optionnel)" type="email" value={form.email} onChange={v => upd('email', v)} testid="a03-email" placeholder="Pour recevoir la confirmation" />
                <Field label="Date souhaitée *" type="date" value={form.date_souhaitee} onChange={v => upd('date_souhaitee', v)} required testid="a03-date" />
                <Field label="Heure souhaitée *" type="time" value={form.heure_souhaitee} onChange={v => upd('heure_souhaitee', v)} required testid="a03-heure" />
                <Field label="Commune de départ *" value={form.commune_depart} onChange={v => upd('commune_depart', v)} required placeholder="Ex : Avermes" testid="a03-depart" />
                <Field label="Destination *" value={form.destination} onChange={v => upd('destination', v)} required placeholder="Ex : CHU Moulins" testid="a03-destination" />
              </div>

              <div>
                <Label className="text-sm font-medium">Type de transport *</Label>
                <RadioGroup value={form.type_transport} onValueChange={v => upd('type_transport', v)} className="mt-2 grid sm:grid-cols-3 gap-2">
                  {[['ambulance', 'Ambulance'], ['vsl', 'VSL'], ['ne_sait_pas', 'Je ne sais pas']].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2.5 hover:bg-anthracite-100/50">
                      <RadioGroupItem value={k} id={`tt-${k}`} />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-sm font-medium">Prescription médicale *</Label>
                <RadioGroup value={form.prescription_medicale} onValueChange={v => upd('prescription_medicale', v)} className="mt-2 grid grid-cols-2 gap-2">
                  {[['oui', 'Oui, j\'en ai une'], ['non', 'Non, pas encore']].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2.5 hover:bg-anthracite-100/50">
                      <RadioGroupItem value={k} id={`pm-${k}`} />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-sm font-medium">Commentaires</Label>
                <Textarea value={form.commentaires} onChange={e => upd('commentaires', e.target.value)} placeholder="Précisions utiles : aller-retour, oxygène, accompagnant, mobilité..." className="mt-1.5 min-h-[90px]" />
              </div>

              <Button type="submit" size="lg" disabled={loading} className="w-full" data-testid="ambulance03-submit">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours…</> : <>Envoyer ma demande <ArrowRight className="h-4 w-4" /></>}
              </Button>

              <div className="pt-2 border-t text-center text-xs text-muted-foreground">
                Vous préférez appeler ? <a href={PHONE_TEL} onClick={() => trackPhoneClick('form-inline', PHONE_DISPLAY)} className="font-semibold text-brand-700 hover:text-brand-900">📞 {PHONE_DISPLAY}</a>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="section-pad bg-white">
        <div className="container-app max-w-3xl">
          <Reveal>
            <div className="text-center">
              <p className="badge-soft mb-3 mx-auto">FAQ</p>
              <h2 className="h2 text-anthracite-950">Vos questions fréquentes</h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <Accordion type="single" collapsible className="mt-8" data-testid="ambulance03-faq">
              {[
                { q: 'Comment réserver un transport sanitaire ?', a: `Le plus simple est d'appeler directement notre équipe au ${PHONE_DISPLAY} ou de remplir le formulaire ci-dessus. Nous vous rappelons ensuite pour confirmer les horaires et les modalités.` },
                { q: 'Une prescription médicale est-elle obligatoire ?', a: "Oui, pour que le transport soit remboursé par l'Assurance Maladie, une prescription médicale (bon de transport CERFA n°50742*04) délivrée par votre médecin est obligatoire, sauf en cas d'urgence vitale. Elle indique le mode de transport à utiliser (ambulance ou VSL)." },
                { q: 'Les transports sont-ils pris en charge par la CPAM ?', a: "Oui. Les transports en ambulance et VSL prescrits sont remboursés à 65% par la Sécurité sociale, 100% en cas d'ALD (Affection Longue Durée : cancer, dialyse, etc.), accident du travail ou maternité. Le complément est généralement pris en charge par votre mutuelle." },
                { q: 'Quelle est la différence entre une ambulance et un VSL ?', a: "L'ambulance est utilisée lorsque le patient doit être transporté en position allongée ou nécessite une surveillance médicale. Le VSL (Véhicule Sanitaire Léger) est adapté aux patients pouvant voyager en position assise mais nécessitant une assistance. Le choix est fait par votre médecin." },
                { q: 'Intervenez-vous à domicile ?', a: "Oui, nous venons vous chercher à votre domicile (ou lieu de départ) et vous accompagnons jusqu'à votre destination (hôpital, clinique, centre de soins). Nous assurons également le retour à domicile." },
                { q: 'Quels secteurs desservez-vous ?', a: "Nous intervenons principalement sur Avermes, Moulins, Saint-Ennemond, Yzeure, Neuvy, Toulon-sur-Allier, Trévol, Bressolles et communes environnantes. Nous couvrons plus largement le département de l'Allier selon disponibilité." },
                { q: 'Peut-on être accompagné ?', a: "Oui, un accompagnant peut être présent lors du transport (généralement un proche). Merci de nous le préciser lors de la réservation pour que nous prévoyions la place nécessaire." },
                { q: 'Quels sont vos horaires ?', a: "Nous sommes joignables tous les jours pour organiser vos transports programmés. Les trajets peuvent être planifiés selon vos besoins (matin, journée, soirée), et nous nous adaptons aux horaires de vos rendez-vous médicaux." },
              ].map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-heading font-semibold text-anthracite-950 hover:text-brand-700" data-testid={`faq-question-${i}`}>{item.q}</AccordionTrigger>
                  <AccordionContent className="text-anthracite-700 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ============ AVIS ============ */}
      {reviews.length > 0 && (
        <section className="section-pad bg-anthracite-100/40">
          <div className="container-app">
            <Reveal>
              <div className="text-center max-w-2xl mx-auto">
                <p className="badge-soft mb-3 mx-auto"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> Avis clients</p>
                <h2 className="h2 text-anthracite-950">Ils nous ont fait confiance</h2>
              </div>
            </Reveal>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={i * 60}>
                  <article className="card-base p-5 h-full flex flex-col">
                    <StarRating rating={r.rating} />
                    <p className="text-sm text-anthracite-900 leading-relaxed mt-3 flex-1">« {r.comment} »</p>
                    <div className="mt-4 pt-3 border-t text-xs">
                      <p className="font-medium text-anthracite-900">{r.author}</p>
                      {r.context && <p className="text-muted-foreground mt-0.5">{r.context}</p>}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ CTA FINAL ============ */}
      <section className="section-pad">
        <div className="container-app">
          <div className="rounded-3xl bg-gradient-to-r from-brand-700 to-brand-900 text-white p-8 sm:p-12">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-heading font-semibold">Un transport à organiser ?</h3>
                <p className="mt-2 text-white/85 max-w-lg">Notre équipe locale vous répond rapidement et vous accompagne dans toutes vos démarches.</p>
                <ul className="mt-4 space-y-1.5 text-sm text-white/80">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-200" /> Rappel dans les meilleurs délais</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-200" /> Prise en charge CPAM directe</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-200" /> Équipe diplômée et locale</li>
                </ul>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackFacebookAmbulance03Click('cta-final')}
                  className="mt-4 inline-flex items-center gap-2 text-sm text-white/90 hover:text-white underline decoration-white/40 hover:decoration-white transition-colors"
                  data-testid="ambulance03-facebook-cta"
                  aria-label="Suivez Ambulance 03 sur Facebook (nouvel onglet)"
                >
                  <Facebook className="h-4 w-4" /> Suivez Ambulance 03 sur Facebook
                </a>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <a
                  href={PHONE_TEL}
                  onClick={() => trackPhoneClick('cta-final', PHONE_DISPLAY)}
                  className="inline-flex items-center gap-2.5 rounded-xl bg-white px-5 py-4 text-brand-800 font-semibold hover:bg-anthracite-100"
                >
                  <Phone className="h-5 w-5" />
                  <span className="flex flex-col items-start leading-none">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-anthracite-500">Appeler</span>
                    <span className="text-base font-heading font-bold">{PHONE_DISPLAY}</span>
                  </span>
                </a>
                <a href="#demande" className="inline-flex items-center gap-2 rounded-xl bg-brand-500 hover:bg-brand-600 px-5 py-4 text-white font-semibold"><Calendar className="h-5 w-5" /> Formulaire</a>
              </div>
            </div>
          </div>

          {/* Discreet link to unified estimator */}
          <div className="mt-4 text-center text-sm text-anthracite-700">
            Besoin d'estimer le coût de votre trajet ?{' '}
            <Link
              to="/estimation-transport"
              className="font-medium text-brand-700 hover:text-brand-900 underline inline-flex items-center gap-1"
              data-testid="ambulance03-estimation-link"
            >
              <Calculator className="h-3.5 w-3.5" /> Simulateur Taxi / VSL / Ambulance
            </Link>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <Ambulance03StickyMobileCTA />
    </div>
  );
}

// ---------- Success screen ----------
function SuccessScreen({ data, onReset }) {
  return (
    <div className="section-pad bg-gradient-to-br from-brand-50 via-white to-brand-50/50 min-h-[70vh] flex items-center">
      <div className="container-app max-w-2xl">
        <div className="card-base p-8 sm:p-10 text-center" data-testid="ambulance03-success">
          <div className="mx-auto h-20 w-20 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shadow-inner">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <h1 className="h2 mt-5 text-anthracite-950">Demande bien reçue ✅</h1>
          <p className="lead mt-3 text-anthracite-700">
            Notre équipe vous rappellera très rapidement au numéro indiqué pour confirmer les modalités de votre transport.
          </p>
          <div className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-brand-50 border border-brand-100 px-5 py-3">
            <span className="text-[11px] uppercase tracking-[0.14em] text-brand-700 font-semibold">Votre référence</span>
            <span className="font-mono font-bold text-lg text-anthracite-950">{data.reference}</span>
          </div>
          {data.ack_sent === false && data.email_sent === false && (
            <p className="mt-4 text-xs text-anthracite-500 max-w-md mx-auto">
              💡 Votre demande est enregistrée en toute sécurité. Nous vous contacterons par téléphone pour la confirmation.
            </p>
          )}
          <div className="mt-8 flex gap-3 justify-center flex-wrap">
            <a
              href={PHONE_TEL}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white px-6 py-3.5 font-semibold shadow-btn transition-all hover:-translate-y-0.5"
              data-testid="success-call-cta"
            >
              <Phone className="h-4 w-4" /> Nous appeler — {PHONE_DISPLAY}
            </a>
            <Button variant="outline" onClick={onReset} data-testid="success-reset-btn">Nouvelle demande</Button>
          </div>
          <p className="mt-6 text-xs text-anthracite-500">
            En cas d'urgence vitale, composez immédiatement le <strong>15 (SAMU)</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------- Ambulance 03 brand block (clean, no duplicate) ----------
function Ambulance03Logo() {
  return (
    <div>
      <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-brand-700 mb-3">
        Notre implantation à Avermes / Saint-Ennemond
      </p>
      <h1 className="font-heading font-extrabold tracking-tight leading-[1.05]" data-testid="ambulance03-brand-title">
        <span className="block text-brand-800 text-4xl sm:text-5xl lg:text-6xl">Ambulance 03</span>
        <span className="block text-anthracite-950 text-2xl sm:text-3xl lg:text-4xl mt-2">
          Avermes · Saint-Ennemond
        </span>
      </h1>
      <div className="mt-4 h-1 w-16 rounded-full bg-brand-500"></div>
    </div>
  );
}

// ---------- Sticky mobile CTA (dedicated) ----------
function Ambulance03StickyMobileCTA() {
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur border-t border-border shadow-[0_-10px_28px_rgba(15,23,32,0.15)] px-3 py-2.5">
      <a
        href={PHONE_TEL}
        onClick={() => { trackPhoneClick('sticky-mobile', PHONE_DISPLAY); trackMobileCallButton(); }}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-500 to-brand-700 text-white font-semibold py-3.5 shadow-btn"
        data-testid="ambulance03-sticky-call"
      >
        <Phone className="h-5 w-5" />
        <span className="flex flex-col items-start leading-none">
          <span className="text-[10px] font-medium text-white/85 uppercase tracking-[0.14em]">Appeler Ambulance 03</span>
          <span className="text-base font-heading font-bold">{PHONE_DISPLAY}</span>
        </span>
      </a>
    </div>
  );
}

// ---------- Helper components ----------
function Field({ label, type = 'text', value, onChange, required, placeholder, testid }) {
  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} className="mt-1.5" data-testid={testid} />
    </div>
  );
}

function CPAMIcon(props) { return <ShieldCheck {...props} />; }

function IconByName({ name, className = 'h-5 w-5' }) {
  // Lazy dynamic import fallback: we already have Ambulance and Car icons imported above.
  // Using inline map keeps it lightweight.
  const map = {
    Ambulance: (p) => <span {...p} aria-hidden="true">🚑</span>,
    Car: (p) => <span {...p} aria-hidden="true">🚙</span>,
  };
  const Comp = map[name];
  return Comp ? <Comp className={className} /> : <ShieldCheck className={className} />;
}
